<template lang="pug">
  .insights-members-location-widget.insights-widget
    .widget-wrapper
      .widget-common.widget-large.widget-full-height
        .widget-title.d-flex.align-items-center
          .d-flex.align-items-baseline
            | {{ title }}
            i.hi.hi-info-filled.ml-5(
              v-if="tooltipContent"
              v-tooltip="tooltipOptions"
              :title="tooltipContent"
            )

          AppSwitch.title-switch.leaderboard-metrics.cursor-pointer.ml-20(
            v-if="!isUserDashboard"
            v-model="usePercentage"
            v-track:click="trackToggleProperties"
            label="Percentages"
            text-position="right"
          )
        .widget-body(:class="bodyClasses")
          template(v-if="isReady")
            template(v-if="!isEmptyWidget")
              div.chart-container(:key="`chart-container-member-location-${usePercentage}`")
                AppChartStacked(
                  :chart-id="`${widgetKey}-vertical-${usePercentage ? 'percentage' : 'count'}`"
                  class="d-flex align-items-end"
                  :sections="chartSections"
                  :chart-data="chartData"
                  custom-class="insights-members-location-widget-popover"
                  show-popovers
                  :show-bar-labels="showBarLabels"
                  :minimum-label-height="20"
                  popover-origin-position="column"
                  popover-trigger="mouseover"
                  :fixed-width-bars="40"
                  :forced-amount-of-bars="90"
                  has-y-axis
                  :tick-count="3"
                  popover-strategy="container"
                  :arrow-size="5"
                )
                  template(#popover-content="{ columnData }")
                    InsightsMembersLocationPopover(:column-data="columnData")
            InsightsNoData(
              v-else
              :type="emptyReason"
            )
        .widget-footer-bordered(v-if="showWidgetBenchmarks")
          InsightsBenchmarksAverages(
            :averages="benchmarksAverages"
            :pins="benchmarkPins"
            :is-loading="!isReady"
            :widget-name="title"
            class="pb-20"
          )
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { formatDate } from '~/utils/time_utils';
import { numericValue, getInsightsPreference, setInsightsPreference } from '~/utils/insights_utils';
import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';

import AppChartStacked from '~/components/AppChartStacked.vue';

export default {
  name: 'InsightsMembersLocationWidget',
  components: {
    AppChartStacked,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
    InsightsBenchmarksAverages: () => import('~/components/insights/InsightsBenchmarksAverages.vue'),
    InsightsMembersLocationPopover: () => import('~/components/insights/InsightsMembersLocationPopover.vue'),
  },
  mixins: [Insights, InsightsTracking],
  data: () => ({
    widgetKey: INSIGHTS.api.membersLocation,
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    usePercentage: false,
  }),
  computed: {
    ...mapGetters({
      widgetData: 'membersLocation',
    }),
    ...mapGetters([
      'organizationId',
      'showWidgetBenchmarks',
      'isUserDashboard'
    ]),
    title() {
      return 'Locations';
    },
    tooltipContent() {
      if (this.isUserDashboard) {
        return 'Displays the daily work location. The location with more than 50% of logged time determines the status for that day.';
      }

      return "Displays member's work location on each date.";
    },
    chartSections() {
      return ['on_site', 'remote'];
    },
    showBarLabels() {
      return !this.isUserDashboard;
    },
    chartData() {
      return this.widgetData?.chart?.map((item, index) => {
        const remotePercentage = item.total ? item.count_remote / item.total : 0;
        const onSitePercentage = item.total ? 1 - remotePercentage : 0;

        let remoteLabel = '';
        let onSiteLabel = '';

        if (this.usePercentage) {
          remoteLabel = `${(remotePercentage * 100).toFixed(0)}%`;
          onSiteLabel = `${(onSitePercentage * 100).toFixed(0)}%`;
        } else {
          remoteLabel = numericValue(item.count_remote, { maxNumberBeforeSymbol: 9999, roundDigits: 1 });
          onSiteLabel = numericValue(item.count_on_site, { maxNumberBeforeSymbol: 9999, roundDigits: 1 });
        }

        return ({
          title: this.isUserDashboard ? null : `${item.total} ${item.total === 1 ? 'member' : 'members'}`,
          label: item.date ? formatDate(dayjs(item.date)) : item.date,
          value: this.usePercentage || this.isUserDashboard ? 100 : item.total,
          remote: {
            value: remotePercentage,
            count: item.count_remote,
            label: remoteLabel,
          },
          on_site: {
            value: onSitePercentage,
            count: item.count_on_site,
            label: onSiteLabel,
          },
          time_remote: item.time_remote,
          time_on_site: item.time_on_site,
          id: index,
        })
      }) || []
    },
    hasRemoteData() {
      return this.widgetData?.score_remote > 0;
    },
    hasOfficeData() {
      return this.widgetData?.score_on_site > 0;
    },
    benchmarksAverages() {
      if (this.isEmptyWidget) {
        return {};
      }

      const all = this.widgetData?.benchmarks || {};
      const result = {};

      // Order matters: keeps chart bars aligned with pin colors (remote on top, on_site on bottom)
      if (this.hasRemoteData) {
        result.remote = all.remote;
      }
      if (this.hasOfficeData) {
        result.on_site = all.on_site;
      }

      return result;
    },
    benchmarkScores() {
      if (this.isEmptyWidget) {
        return {};
      }
      const scores = {};

      if (this.hasRemoteData) {
        scores.remote = this.widgetData.score_remote;
      }
      if (this.hasOfficeData) {
        scores.on_site = this.widgetData.score_on_site;
      }

      return scores;
    },
    benchmarkPins() {
      const scores = this.benchmarkScores;

      return Object.entries(scores).map(([key, value]) => ({ key, value }));
    },
    trackToggleProperties() {
      return {
        label: 'Members location percentage toggle clicked',
        status: this.usePercentage ? 'on' : 'off',
      };
    },
  },
  watch: {
    usePercentage(value) {
      setInsightsPreference(this.organizationId, 'members_location_use_percentage', value);
    },
  },
  created() {
    this.usePercentage = getInsightsPreference(this.organizationId, 'members_location_use_percentage') ?? false;
  },
};
</script>
